import React from 'react';
import {
  Box,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';

import { PatientSearchResult } from './types';
import { plural } from './format';

interface Props {
  query: string;
  onQueryChange: (value: string) => void;
  results: PatientSearchResult[];
  loading: boolean;
  searched: boolean;
  minQueryLength: number;
  selected: PatientSearchResult | null;
  onSelect: (patient: PatientSearchResult) => void;
}

/**
 * SQ-166, step 1 — WHO.
 *
 * Nothing is preloaded: the resident generates every option below, so until one is
 * picked there is nothing to show. Search is server-side (SQ-160) because the old
 * screen could only filter client-side by fetching the whole facility first.
 */
export const PatientSearch: React.FC<Props> = ({
  query,
  onQueryChange,
  results,
  loading,
  searched,
  minQueryLength,
  selected,
  onSelect,
}) => {
  //  The dropdown is a transient "while searching" affordance, not a reflection of
  //  `results`: once a resident is picked it must close even though the re-search the
  //  new query text triggers still comes back non-empty. It reopens the moment the user
  //  types again.
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  const handleQueryChange = (value: string) => {
    setDropdownOpen(true);
    onQueryChange(value);
  };

  const handleSelect = (patient: PatientSearchResult) => {
    setDropdownOpen(false);
    onSelect(patient);
  };

  return (
    <Box>
      <TextField
        fullWidth
        hiddenLabel
        size="small"
        placeholder="Search by first name, last name or MRN…"
        value={query}
        onChange={(e) => handleQueryChange(e.target.value)}
        inputProps={{ 'aria-label': 'Search residents' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search
                fontSize="small"
                sx={{ color: 'text.disabled' }}
              />
            </InputAdornment>
          ),
          endAdornment: loading ? (
            <InputAdornment position="end">
              <CircularProgress size={16} />
            </InputAdornment>
          ) : null,
        }}
        sx={{ maxWidth: 520 }}
      />

      {!searched && !selected && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1.5 }}
        >
          Type at least {minQueryLength} characters to find a resident.
        </Typography>
      )}

      {dropdownOpen && searched && !loading && results.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1.5 }}
        >
          No residents match “{query.trim()}”.
        </Typography>
      )}

      {dropdownOpen && results.length > 0 && (
        <List
          dense
          disablePadding
          sx={{
            mt: 1.5,
            maxWidth: 720,
            maxHeight: 280,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          {results.map((p) => {
            const isSelected = selected?.uuid === p.uuid;
            return (
              <ListItemButton
                key={p.uuid}
                selected={isSelected}
                onClick={() => handleSelect(p)}
                sx={{ py: 1.25 }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                  sx={{ width: '100%' }}
                >
                  <Stack
                    direction="row"
                    alignItems="baseline"
                    spacing={1}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 500 }}
                    >
                      {p.lastName}, {p.firstName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      {p.mrn || '—'}
                    </Typography>
                  </Stack>
                  {/* A resident with nothing on file is shown, badged zero, rather than
                    hidden — otherwise the user hunts for someone who is not there. */}
                  <Typography
                    variant="body2"
                    // color={p.documentCount > 0 ? 'text.secondary' : 'warning.main'}
                    color={'text.secondary'}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {p.payerName && `${p.payerName} · `}
                    {plural(p.documentCount, 'document')}
                  </Typography>
                </Stack>
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );
};
